import { LelandPlusBanner as LulLelandPlusBanner } from '@leland-dev/leland-ui-library';
import { type PortableTextTypeComponentProps } from '@portabletext/react';
import { type FC } from 'react';

import { type Scalars } from '../../../__generated-gql-types__/globalTypes';
import LelandPlusLogo from '../../../assets/svgs/leland-plus-logo.svg';
import { categorySlugToUrn } from '../../../utils/category';
import {
  CAREER_CATEGORY_SLUG,
  COLLEGE_CATEGORY_SLUG,
  MANAGEMENT_CONSULTING_CATEGORY_SLUG,
  MBA_CATEGORY_SLUG,
  PRODUCT_MANAGEMENT_CATEGORY_SLUG,
} from '../../../utils/constants';
import { getLelandPlusLandingPageUrl } from '../../../utils/leland-plus';
import { getCoachProfileUrl } from '../../../utils/routing';
import {
  LelandPlusCareerImages,
  LelandPlusConsultingImages,
  LelandPlusProductManagementImages,
  LelandPlusSchoolImages,
} from '../../common/LelandPlusImages';

import {
  type LelandPlusBannerCoachBatchResponseFragment,
  useLelandPlusBannerUserQueryQuery,
} from './__generated-gql-types__/LelandPlusBanner.generated';

interface LelandPlusBannerProps {
  articleSlug: Possible<string>;
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>;
  contributors: LelandPlusBannerCoachBatchResponseFragment;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

function isManagementConsultingArticle(
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>,
) {
  const managementConsultingCategoryUrn = categorySlugToUrn(
    MANAGEMENT_CONSULTING_CATEGORY_SLUG,
  );
  return !!categories?.some(
    (categoryUrn) => categoryUrn === managementConsultingCategoryUrn,
  );
}

function isCollegeArticle(
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>,
) {
  const collegeCategoryUrn = categorySlugToUrn(COLLEGE_CATEGORY_SLUG);
  return !!categories?.some(
    (categoryUrn) => categoryUrn === collegeCategoryUrn,
  );
}

function isMbaArticle(
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>,
) {
  const mbaCategoryUrn = categorySlugToUrn(MBA_CATEGORY_SLUG);
  return !!categories?.some((categoryUrn) => categoryUrn === mbaCategoryUrn);
}

function isProductManagementArticle(
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>,
) {
  const pmCategoryUrn = categorySlugToUrn(PRODUCT_MANAGEMENT_CATEGORY_SLUG);
  return !!categories?.some((categoryUrn) => categoryUrn === pmCategoryUrn);
}

function isGeneralCareerArticle(
  categories: Possible<Array<Scalars['CategoryUrn']['output']>>,
) {
  const careerCategoryUrn = categorySlugToUrn(CAREER_CATEGORY_SLUG);
  return !!categories?.some((categoryUrn) => categoryUrn === careerCategoryUrn);
}

export const LelandPlusBanner: FC<
  PortableTextTypeComponentProps<unknown> & LelandPlusBannerProps
> = ({ articleSlug, categories, onClick, contributors }) => {
  const { data: userData } = useLelandPlusBannerUserQueryQuery();

  const isLelandPlusUser = !!userData?.user?.applicant?.lelandPlus?.active;

  return (
    <div className="my-10">
      <LulLelandPlusBanner
        hideFreeTrial={isLelandPlusUser}
        numCoaches={contributors.total}
        featuredCoaches={contributors?.results?.map((coach) => {
          return {
            id: coach.id,
            firstName: coach.user.firstName,
            lastName: coach.user.lastName,
            slug: coach.slug,
            pictureLink: coach.user.pictureLink ?? undefined,
            url: getCoachProfileUrl(coach.slug),
          };
        })}
        ctaLabel={isLelandPlusUser ? 'Go to Leland+' : 'Get started for free'}
        landingPageUrl={getLelandPlusLandingPageUrl({
          isLelandPlusUser,
          categorySlug: isManagementConsultingArticle(categories)
            ? MANAGEMENT_CONSULTING_CATEGORY_SLUG
            : isCollegeArticle(categories)
              ? COLLEGE_CATEGORY_SLUG
              : isProductManagementArticle(categories)
                ? PRODUCT_MANAGEMENT_CATEGORY_SLUG
                : isMbaArticle(categories)
                  ? MBA_CATEGORY_SLUG
                  : isGeneralCareerArticle(categories)
                    ? CAREER_CATEGORY_SLUG
                    : undefined,
          utmMedium: 'article',
          utmCampaign: articleSlug ?? 'unknown-article',
        })}
        lelandPlusIcon={<LelandPlusLogo alt="Leland Plus Logo" />}
        onClick={onClick}
        imageCtas={
          isManagementConsultingArticle(categories)
            ? LelandPlusConsultingImages
            : isCollegeArticle(categories) || isMbaArticle(categories)
              ? LelandPlusSchoolImages
              : isProductManagementArticle(categories)
                ? LelandPlusProductManagementImages
                : LelandPlusCareerImages
        }
      />
    </div>
  );
};
